import Immutable from 'immutable';
import { AppState, AppStateActionParams } from 'records/AppState';
import {
  CreateMappedSheetVersionInput,
  MappedSheetVersion,
} from 'records/MappedSheetVersion';
import { PageId } from 'records/Page';
import { SheetVersion } from 'records/SheetVersion';
import {
  createMappedSheetVersion,
  getMappedSheetVersions,
  removeMappedSheetVersion,
} from 'services/mappedSheetVersionService';

export interface MappedSheetActions {
  createMappedSheetVersion: (
    { state }: AppStateActionParams,
    pageid: string,
    imagecalibrationconfig: CreateMappedSheetVersionInput
  ) => Promise<AppState>;

  getMappedSheetVersions: (
    actionParams: AppStateActionParams
  ) => Promise<AppState>;

  setVisibleMappedSheetVersions: (
    actionParams: AppStateActionParams,
    visibleMappedSheetVersions: Immutable.Set<PageId>
  ) => AppState;

  removeMappedSheetVersion: (
    { state }: AppStateActionParams,
    pageid: string
  ) => Promise<AppState>;
}

const mappedSheetActions: MappedSheetActions = {
  createMappedSheetVersion: async (
    { state },
    pageid,
    imagecalibrationconfig
  ) => {
    const { selectedProject, visibleMappedSheetVersions, sheets, rfiQuery } =
      state();

    const mappedSheetVersion = await createMappedSheetVersion(
      selectedProject,
      pageid,
      imagecalibrationconfig
    );

    const sheetVersion = state().getSheetVersionByPageId(
      mappedSheetVersion.pageid
    );

    const sheetVersions =
      sheets.get(sheetVersion.sheetnumber)?.versions ||
      Immutable.List<SheetVersion>();

    const sheetVersionIndex = sheetVersions.indexOf(sheetVersion);

    const newQueryPages =
      rfiQuery.pages?.add(mappedSheetVersion.pageid) || null;

    return state()
      .setIn(
        [
          'sheets',
          sheetVersion.sheetnumber,
          'versions',
          sheetVersionIndex,
          'maptilecloudfronturl',
        ],
        ''
      )
      .setIn(
        ['mappedSheetVersions', mappedSheetVersion.pageid],
        mappedSheetVersion
      )
      .set(
        'visibleMappedSheetVersions',
        visibleMappedSheetVersions.add(mappedSheetVersion.pageid)
      )
      .set('rfiQuery', rfiQuery.set('pages', newQueryPages));
  },
  getMappedSheetVersions: async ({
    state,
  }: AppStateActionParams): Promise<AppState> => {
    const { selectedProject } = state();
    const mappedSheetVersions = await getMappedSheetVersions(selectedProject);

    const mappedSheetVersionsMap = mappedSheetVersions.reduce(
      (acc, mappedSheetVersion) => {
        return acc.set(mappedSheetVersion.pageid, mappedSheetVersion);
      },
      Immutable.OrderedMap<string, MappedSheetVersion>()
    );

    return state().set('mappedSheetVersions', mappedSheetVersionsMap);
  },
  setVisibleMappedSheetVersions: (
    { state }: AppStateActionParams,
    visibleMappedSheetVersions: Immutable.Set<PageId>
  ): AppState => {
    const { rfiQuery } = state();

    return state()
      .set('visibleMappedSheetVersions', visibleMappedSheetVersions)
      .set('rfiQuery', rfiQuery.set('pages', visibleMappedSheetVersions));
  },
  removeMappedSheetVersion: async ({ state }, pageid) => {
    const {
      selectedProject,
      visibleMappedSheetVersions,
      mappedSheetVersions,
      rfiQuery,
    } = state();

    const newQueryPages = rfiQuery.pages?.remove(pageid) || null;

    await removeMappedSheetVersion(selectedProject, pageid);

    return state()
      .set('mappedSheetVersions', mappedSheetVersions.remove(pageid))
      .set(
        'visibleMappedSheetVersions',
        visibleMappedSheetVersions.remove(pageid)
      )
      .set('rfiQuery', rfiQuery.set('pages', newQueryPages));
  },
};

export default mappedSheetActions;
